import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';

import { OWNERS_HUB_ROUTES } from '@/constants/routes';
import store from '@/store';
import { authenticationRefresh, logout } from '@/store/slices/authentication';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Per-request opt-out from the global auth teardown. A 401 on a configurator-session
     * endpoint can mean "THIS resource's session is invalid" (e.g. uploading a logo
     * against a launched brand's terminal deploy session) rather than "your access token
     * expired". Without this, the interceptor would rotate the token, replay, get the same
     * 401, conclude auth is gone, and log the whole app out. Setting this still allows the
     * token-refresh recovery (a genuine expiry self-heals) but, if recovery fails, REJECTS
     * to the caller instead of tearing the session down — so the call surfaces its own error.
     */
    _owsNoAuthTeardown?: boolean;
    /**
     * Per-request opt-out from the ENTIRE owner-hub auth recovery: NEITHER the
     * refresh-and-replay NOR the teardown. Used for calls to a SEPARATE service, either
     * through the owner-hub MLP proxy to a downstream service (Casino Ad / CRM Events via
     * `utils/mlp-proxy.ts`) or DIRECTLY on its own host (the Lumio chat service via
     * `nova/…/LumioChat/endpoint.ts`). For those, a 401/403 can come from the OTHER service
     * and is not evidence the owner-hub session expired: rotating the owner-hub token can't
     * fix the other service's auth, and tearing the session down would log the user out over
     * an unrelated service's hiccup. With this set, the interceptor rejects the 401/403
     * straight to the caller, which owns the failure (Lumio fails silently into its own retry
     * affordance). Stronger than `_owsNoAuthTeardown`, which still refreshes the token first.
     */
    _owsNoAuthRecovery?: boolean;
  }
}

/**
 * Global stale-session guard with reactive token refresh.
 *
 * Auth used to be validated/rotated exactly once, at boot (`APPLICATION_INITIALIZE`
 * → refresh + check). After that there was NO reactive refresh: an access token that
 * aged past its TTL mid-session produced a 401 on the next API call, and this
 * interceptor responded by tearing the session down and sending the user to the
 * registration funnel. In a long single-page flow (e.g. the configurator → payment →
 * the multi-minute onboarding deploy, with no page reload to trigger the boot
 * refresh) that meant a routine expiry ejected a paying, account-holding user to the
 * sign-UP page mid-deploy.
 *
 * This interceptor now RECOVERS first: on a 401 it rotates the token via the existing
 * `refresh_token` (single-flight — see below) and REPLAYS the original request with the
 * fresh token, so a mid-session expiry self-heals invisibly. Only when recovery is
 * impossible (no refresh token, the refresh failed, or the refreshed token ALSO 401'd)
 * does it fall back to the old behaviour: tear the session down and send the user to
 * `/login` (they have an account — re-authenticate, don't sign up).
 *
 * Single-flight: the backend ROTATES refresh tokens — issuing a new one deletes the old
 * refresh token AND invalidates the old access token — so N requests 401'ing at once must
 * share ONE refresh. If each fired its own, only the first would succeed and the rest would
 * kill the freshly-minted token. `refreshInFlight` dedupes them onto a single promise.
 *
 * Only 401 is recoverable: a 403 is a genuine permission denial that refreshing can't fix,
 * so it skips straight to teardown (unchanged from before, bar the /login destination).
 *
 * Excluded: the auth endpoints themselves (`/auth/check`, `/auth/refresh`, `/auth/login`,
 * `/auth/link/...`). A 401 there is the EXPECTED "not logged in" signal handled by the auth
 * slice / saga — acting on it would fight the boot check and risk a redirect loop, and it
 * keeps the refresh call itself from recursing into recovery. (`userApiUrl + '/auth'` is the
 * shared prefix.) Also excluded: when already on `/login` / `/register` (no self-redirect).
 */
const isAuthEndpoint = (url?: string): boolean => !!url && url.includes('/auth/');

const isOnAuthPage = (): boolean =>
  typeof window !== 'undefined' &&
  /\/(register|login|telegram-callback|verify-email)(?:\/|$)/.test(window.location.pathname);

/** Per-request marker: this request was already retried once with a refreshed token. */
type RetriableConfig = InternalAxiosRequestConfig & { _owsAuthRetry?: boolean };

/**
 * Shared, single-flight token refresh. Concurrent 401s all await the SAME rotation so the
 * backend's refresh-token rotation can't invalidate a sibling's freshly-minted token.
 * Resolves `true` when the session now holds a valid, refreshed token.
 */
let refreshInFlight: Promise<boolean> | null = null;

const refreshOnce = (): Promise<boolean> => {
  if (!refreshInFlight) {
    refreshInFlight = store
      .dispatch(authenticationRefresh())
      .unwrap()
      // The thunk FULFILLS even when it can't refresh (no refresh token → success:false),
      // so gate on the success flag, not merely on "didn't throw".
      .then((result) => Boolean(result && result.success))
      .catch(() => false)
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

const redirectToLogin = (): void => {
  // Prefer the SPA router (no full reload); fall back to a hard navigation if the
  // router has not been created yet (e.g. an API call rejected before first paint).
  // Imported lazily so this module does not pull in the route tree at load time.
  import('@/routes')
    .then(({ storedRouter }) => {
      if (storedRouter) {
        storedRouter.navigate(OWNERS_HUB_ROUTES.LOGIN, { replace: true });
      } else if (typeof window !== 'undefined') {
        window.location.assign(OWNERS_HUB_ROUTES.LOGIN);
      }
    })
    .catch(() => {
      if (typeof window !== 'undefined') {
        window.location.assign(OWNERS_HUB_ROUTES.LOGIN);
      }
    });
};

/** Tear the session down (single chokepoint — the store listener also clears the
 *  configurator session + brand state) and send the user to sign back in. */
const tearDownSession = (): void => {
  store.dispatch(logout());
  redirectToLogin();
};

export const installAuthInterceptor = (client: AxiosInstance): void => {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const status = error?.response?.status;
      const original = error?.config as RetriableConfig | undefined;
      const url = original?.url;

      const eligible = (status === 401 || status === 403) && !isAuthEndpoint(url) && !isOnAuthPage();
      if (!eligible || !original) {
        return Promise.reject(error);
      }

      // Calls to a separate service on its own host (e.g. Lumio) own their auth failures
      // end-to-end: a 401/403 from THEIR host says nothing about the owner-hub session, so
      // don't rotate the owner-hub token and don't tear the session down — reject straight to
      // the caller, which surfaces its own error. Checked before recovery so no refresh fires.
      if (original._owsNoAuthRecovery) {
        return Promise.reject(error);
      }

      // A 401 (expired/invalid access token) is recoverable by rotating the token — but
      // only once per request (if the refreshed token ALSO 401'd, the session is genuinely
      // gone) and only when we actually have a refresh token to rotate. A 403 is a real
      // permission denial that refreshing won't fix.
      const hasRefreshToken = store.getState().authentication.refresh_token != null;
      const canRecover = status === 401 && hasRefreshToken && !original._owsAuthRetry;

      if (canRecover) {
        const refreshed = await refreshOnce();
        const newToken = store.getState().authentication.access_token;
        if (refreshed && newToken != null) {
          original._owsAuthRetry = true;
          // Preserve the caller's Authorization shape: session calls send the RAW token,
          // some data-source calls send "Bearer <token>". Replaying with the wrong shape
          // would just 401 again.
          const previous =
            (original.headers && (original.headers.Authorization ?? original.headers.authorization)) ?? '';
          const useBearer = typeof previous === 'string' && previous.startsWith('Bearer ');
          if (!original.headers) {
            original.headers = {} as RetriableConfig['headers'];
          }
          original.headers.Authorization = useBearer ? `Bearer ${newToken}` : newToken;
          // Replay once with the fresh token; its response resolves the original caller
          // transparently, so the mid-session expiry is invisible to the user.
          return client(original);
        }
      }

      // Unrecoverable: token gone and can't be refreshed (no refresh token, refresh failed,
      // a 403, or a retry that still 401'd). A request that opted out of teardown owns its
      // own auth errors — a session-scoped 401 there means "this resource's session is
      // invalid", not "you're logged out", so reject to the caller without ejecting them.
      if (original._owsNoAuthTeardown) {
        return Promise.reject(error);
      }
      tearDownSession();
      return Promise.reject(error);
    },
  );
};

export default installAuthInterceptor;
